import React from 'react';
import {
  Box,
  Button,
  Grid,
  CircularProgress
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { translationActions } from '../../../store/translationSlice';
import ReferenceComponent from './referenceComponent';
import CaptionList from './CaptionList';
import LanguageSelection from './LanguageSelection';
import SuccessDisplay from './SuccessDisplay';
import ErrorAlert from '../atom/ErrorAlert';
import { checkCaptionForUniqueId } from '../services/captionApiService';
import { 
  FIND_MY_CAPTIONS, 
  REFERENCE_ID_REQUIRED, 
  REFERENCE_ID_MIN_LENGTH,
  ERROR_FIND_CAPTIONS,
  ERROR_SELECT_CAPTION,
  ERROR_TRANSLATE_CAPTION,
  ERROR_DOWNLOAD_CAPTION
} from '../constants/TranslationConstants';

interface RightSectionProps {
  step: number;
}

const RightSection = ({ step }: RightSectionProps) => {
    const dispatch = useDispatch();
    const {
        referenceId,
        selectedCaption,
        translatedFileName,
        validationError,
        apiError,
        isLoading,
        captionsData
    } = useSelector((state: any) => state.translation);

const handleFindCaptions = async () => {
        // Clear previous validation errors
        dispatch(translationActions.clearValidationError());
        dispatch(translationActions.clearApiError());
        
        // Validate reference ID
        if (!referenceId.trim()) {
            dispatch(translationActions.setValidationError(REFERENCE_ID_REQUIRED));
            return;
        }
        
        if (referenceId.trim().length < 3) {
            dispatch(translationActions.setValidationError(REFERENCE_ID_MIN_LENGTH));
            return;
        }
        
        // Start loading
        dispatch(translationActions.setIsLoading(true));
        
        try {
            console.log('Finding captions for:', referenceId);
            
            // Call real API
            const result = await checkCaptionForUniqueId(referenceId.trim());
            
            if (result.success && result.captions && result.captions.length > 0) {
                // Success: captions found
                dispatch(translationActions.setCaptionsData(result.captions));
                dispatch(translationActions.setCurrentStep(2)); // Move to caption selection screen
            } else {
                // No captions found or API returned error message
                dispatch(translationActions.setApiError(result.message || 'No captions were available for this reference ID'));
            }
        } catch (error) {
            console.error('API Error:', error);
            dispatch(translationActions.setApiError(ERROR_FIND_CAPTIONS));
        } finally {
            // Stop loading regardless of success or failure
            dispatch(translationActions.setIsLoading(false));
        }
    };

    const handleSelectCaption = async (selectedCaption: any) => {
        dispatch(translationActions.clearApiError());
        try {
            console.log('Selected caption:', selectedCaption);
            dispatch(translationActions.setSelectedCaption(selectedCaption));
            // Simulate API call to validate caption selection
            // await apiService.validateCaption(selectedCaption.id);
            
            // Move to step 3 - showing language selection
            dispatch(translationActions.setCurrentStep(3));
        } catch (error) {
            dispatch(translationActions.setApiError(ERROR_SELECT_CAPTION));
        }
    };

    const handleTranslate = async (targetLanguage: string) => {
        dispatch(translationActions.clearApiError());
        dispatch(translationActions.setIsTranslating(true));
        
        try {
            console.log('Translating to:', targetLanguage);
            console.log('Caption:', selectedCaption);
            // Simulate translation API call
            // const result = await apiService.translateCaption(selectedCaption.id, targetLanguage);
            
            // Simulate network delay for translation
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            // Simulate translation process
            dispatch(translationActions.setTranslatedFileName(`${selectedCaption.fileName}`));
            // Move to step 4 - showing success page
            dispatch(translationActions.setCurrentStep(4));
        } catch (error) {
            dispatch(translationActions.setApiError(ERROR_TRANSLATE_CAPTION));
        } finally {
            dispatch(translationActions.setIsTranslating(false));
        }
    };

    const handleDownload = async () => {
        dispatch(translationActions.clearApiError());
        try {
            console.log('Downloading file:', translatedFileName);
            // Simulate API call to get download URL
            // const downloadUrl = await apiService.getDownloadUrl(translatedFileName);
            
            // Handle file download logic here
            const element = document.createElement('a');
            const file = new Blob(['Translated caption content...'], { type: 'text/plain' });
            element.href = URL.createObjectURL(file);
            element.download = translatedFileName;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        } catch (error) {
            dispatch(translationActions.setApiError(ERROR_DOWNLOAD_CAPTION));
        }
    };

    const handleRestart = () => {
        // Reset all state to initial values using Redux action
        dispatch(translationActions.resetTranslationState());
    };

  return <> 
        <Grid item xs={12} md={8}>
            <Box sx={{ pt: { xs: 1, md: 0 } }}>
              {step === 1 ? (
                <>
                  <ErrorAlert 
                    message={apiError} 
                    onClose={() => dispatch(translationActions.clearApiError())} 
                    show={!!apiError} 
                  />
                  <ReferenceComponent 
                    referenceId={referenceId} 
                    setReferenceId={(value: string) => dispatch(translationActions.setReferenceId(value))}
                    validationError={validationError}
                    clearValidationError={() => dispatch(translationActions.clearValidationError())}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleFindCaptions}
                      disabled={!referenceId.trim() || referenceId.trim().length < 3 || isLoading}
                      size="small"
                      sx={{
                        fontSize: "10px",
                        background: "#006684",
                        height: "30px",
                        color: "#FFFFFF",
                        cursor: "pointer",
                        textTransform: "capitalize",
                        fontWeight: "600",
                        minWidth: "140px",
                        '&:disabled': {
                          backgroundColor: '#ccc',
                          color: '#666',
                          cursor: 'not-allowed'
                        },
                        '&:hover:not(:disabled)': {
                          backgroundColor: '#005a70'
                        }
                      }}
                      startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : null}
                    >
                      {isLoading ? 'Loading...' : FIND_MY_CAPTIONS}
                    </Button>
                  </Box>
                </>
              ) : step === 2 ? (
                <>
                  <ErrorAlert 
                    message={apiError} 
                    onClose={() => dispatch(translationActions.clearApiError())} 
                    show={!!apiError} 
                  />
                  <CaptionList 
                    referenceId={referenceId}
                    onSelect={handleSelectCaption}
                    captionsData={captionsData}
                  />
                </>
              ) : step === 3 ? (
                <>
                  <ErrorAlert 
                    message={apiError} 
                    onClose={() => dispatch(translationActions.clearApiError())} 
                    show={!!apiError} 
                  />
                  <LanguageSelection
                    selectedCaption={selectedCaption}
                    onTranslate={handleTranslate}
                  />
                </>
              ) : (
                <>
                  <ErrorAlert 
                    message={apiError} 
                    onClose={() => dispatch(translationActions.clearApiError())} 
                    show={!!apiError} 
                  />
                  <SuccessDisplay
                    fileName={selectedCaption?.fileName || 'caption-file.srt'}
                    sourceLanguage={selectedCaption?.language || 'English'}
                    onDownload={handleDownload}
                    onRestart={handleRestart}
                  />
                </>
              )}
            </Box>
        </Grid>
    </>
}

export default RightSection;